<script setup lang="ts">
import { computed } from 'vue';
import { t } from './i18n';
import { resolveSelections } from './mappingEngine';
import type { Choice, ConfiguratorOption, ConfiguratorProfile, Selections } from './types';

/*
 * Shopper-facing options panel, shared by the quick personalizer (product
 * page) and the designer's "Options" tab. Uses its own `vpsc:` Tailwind
 * prefix and stylesheet so it never overrides the host's `vptw:` utilities.
 */
const props = defineProps<{
  profile: ConfiguratorProfile;
  modelValue: Selections;
  money: (amount: number) => string;
  showErrors?: boolean;
  heading?: boolean;
}>();
const emit = defineEmits<{ 'update:modelValue': [value: Selections] }>();

const resolved = computed(() => resolveSelections(props.profile, props.modelValue, (label) => t('Choose "{label}".').replace('{label}', label)));
const visibleOptions = computed(() => props.profile.options.filter((option) => resolved.value.visible.includes(option.id)));

function set(option: ConfiguratorOption, value: string | boolean) {
  emit('update:modelValue', { ...props.modelValue, [option.id]: value });
}

function priceTag(amount: number, per: string): string {
  if (!amount || !Number.isFinite(amount)) return '';
  const money = props.money(Math.abs(amount));
  if (amount > 0) return per === 'line' ? t('+{price} once').replace('{price}', money) : `+${money}`;
  return per === 'line' ? t('−{price} once').replace('{price}', money) : `−${money}`;
}

/** Screen-reader label: "XXL, adds $2.50" instead of a bare corner badge. */
function choiceLabel(choice: Choice): string {
  const tag = priceTag(choice.price, choice.per);
  return tag ? `${choice.label}, ${tag}` : choice.label;
}

function selected(option: ConfiguratorOption): string | boolean {
  return resolved.value.selections[option.id] ?? '';
}

function selectedChoice(option: ConfiguratorOption): Choice | undefined {
  return option.choices.find((choice) => choice.value === String(selected(option)));
}

function missing(option: ConfiguratorOption): boolean {
  const value = selected(option);
  return Boolean(props.showErrors) && option.required && (value === '' || value === false);
}

function inputValue(event: Event): string {
  return (event.target as HTMLInputElement | HTMLSelectElement).value;
}
</script>

<template>
  <section class="veyop-sc vpsc:space-y-4 vpsc:text-(--vs-ink)" :aria-label="profile.title || t('Product options')">
    <header v-if="heading" class="vpsc:space-y-0.5">
      <h3 class="vpsc:m-0! vpsc:p-0! vpsc:text-base! vpsc:leading-6! vpsc:font-semibold">{{ profile.title || t('Product options') }}</h3>
    </header>

    <div v-for="option in visibleOptions" :key="option.id" class="vpsc:space-y-2">
      <!-- Checkbox options render as a single toggle row -->
      <label v-if="option.type === 'checkbox'" class="vpsc:flex vpsc:cursor-pointer vpsc:items-start vpsc:gap-3 vpsc:rounded-xl vpsc:border vpsc:border-(--vs-line) vpsc:bg-white vpsc:px-3 vpsc:py-2.5" :class="selected(option) === true ? 'vpsc:border-(--vs-line) vpsc:ring-1 vpsc:ring-gray-900' : ''">
        <input type="checkbox" class="vpsc:mt-0.5! vpsc:size-4 vpsc:shrink-0 vpsc:cursor-pointer vpsc:accent-gray-900" :checked="selected(option) === true" @change="set(option, ($event.target as HTMLInputElement).checked)" />
        <span class="vpsc:min-w-0 vpsc:flex-1">
          <span class="vpsc:block vpsc:text-sm vpsc:font-medium">{{ option.label }}</span>
          <span v-if="option.help" class="vpsc:mt-0.5 vpsc:block vpsc:text-xs vpsc:text-(--vs-ink-2)">{{ option.help }}</span>
        </span>
        <span v-if="priceTag(option.price ?? 0, option.per ?? 'unit')" class="vpsc:shrink-0 vpsc:text-sm vpsc:font-medium vpsc:text-(--vs-ink-2)">{{ priceTag(option.price ?? 0, option.per ?? 'unit') }}</span>
      </label>

      <template v-else>
        <div class="vpsc:flex vpsc:items-baseline vpsc:justify-between vpsc:gap-2">
          <span class="vpsc:text-sm vpsc:font-medium">
            {{ option.label }}<span v-if="option.required" class="vpsc:text-red-600"> *</span>
          </span>
          <span v-if="option.type === 'swatch' || option.type === 'image'" class="vpsc:truncate vpsc:text-xs vpsc:text-(--vs-ink-2)">{{ selectedChoice(option)?.label }}</span>
          <span v-if="option.type === 'text' && option.max_length" class="vpsc:text-xs vpsc:tabular-nums vpsc:text-(--vs-ink-3)">{{ String(selected(option)).length }}/{{ option.max_length }}</span>
        </div>
        <p v-if="option.help" class="vpsc:m-0! vpsc:-mt-1! vpsc:text-xs vpsc:text-(--vs-ink-2)">{{ option.help }}</p>

        <select
          v-if="option.type === 'select'"
          :aria-label="option.label"
          class="vpsc:w-full vpsc:max-w-none! vpsc:cursor-pointer vpsc:rounded-xl! vpsc:border! vpsc:border-(--vs-line)! vpsc:bg-white! vpsc:px-3! vpsc:py-2! vpsc:text-sm vpsc:shadow-none! vpsc:appearance-none vpsc:bg-[url(data:image/svg+xml,%3csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20fill=%22none%22%20viewBox=%220%200%2024%2024%22%20stroke-width=%221.5%22%20stroke=%22%239ca3af%22%3e%3cpath%20stroke-linecap=%22round%22%20stroke-linejoin=%22round%22%20d=%22M8.25%2015%2012%2018.75%2015.75%2015m-7.5-6L12%205.25%2015.75%209%22/%3e%3c/svg%3e)] vpsc:bg-no-repeat vpsc:bg-position-[right_0.5rem_center] vpsc:rtl:bg-position-[left_0.5rem_center] vpsc:bg-size-[1.25rem] vpsc:pe-8!"
          :value="selected(option)"
          @change="set(option, inputValue($event))"
        >
          <option v-if="!option.default && option.required" value="" disabled>{{ t('Choose…') }}</option>
          <option v-for="choice in option.choices" :key="choice.value" :value="choice.value">
            {{ choice.label }}{{ priceTag(choice.price, choice.per) ? ` (${priceTag(choice.price, choice.per)})` : '' }}
          </option>
        </select>

        <div v-else-if="option.type === 'buttons'" class="vpsc-choices" role="radiogroup" v-bind="{ 'aria-label': option.label }">
          <button
            v-for="choice in option.choices"
            :key="choice.value"
            type="button"
            role="radio"
            class="vpsc-chip"
            :aria-checked="selected(option) === choice.value"
            :aria-label="choiceLabel(choice)"
            @click="set(option, choice.value)"
          >
            {{ choice.label }}
            <span v-if="priceTag(choice.price, choice.per)" class="vpsc-price" :class="{ 'vpsc-price--discount': choice.price < 0 }" aria-hidden="true">{{ priceTag(choice.price, choice.per) }}</span>
          </button>
        </div>

        <div v-else-if="option.type === 'swatch'" class="vpsc-choices" role="radiogroup" v-bind="{ 'aria-label': option.label }">
          <button
            v-for="choice in option.choices"
            :key="choice.value"
            type="button"
            role="radio"
            :aria-checked="selected(option) === choice.value"
            :title="choiceLabel(choice)"
            :aria-label="choiceLabel(choice)"
            class="vpsc-swatch vpsc:size-9 vpsc:rounded-full! vpsc:border-2! vpsc:border-(--vs-line)! vpsc:p-0.5! vpsc:cursor-pointer vpsc:bg-white! vptw:min-w-max!"
            @click="set(option, choice.value)"
          >
            <span class="vpsc:block vpsc:size-full vpsc:rounded-full vpsc:ring-1 vpsc:ring-black/10" :style="{ backgroundColor: choice.color || choice.value }" />
            <span v-if="priceTag(choice.price, choice.per)" class="vpsc-price" :class="{ 'vpsc-price--discount': choice.price < 0 }" aria-hidden="true">{{ priceTag(choice.price, choice.per) }}</span>
          </button>
        </div>

        <div v-else-if="option.type === 'image'" class="vpsc:grid vpsc:grid-cols-3 vpsc:gap-2 vpsc:pt-2 vpsc:sm:grid-cols-4" role="radiogroup" v-bind="{ 'aria-label': option.label }">
          <button
            v-for="choice in option.choices"
            :key="choice.value"
            type="button"
            role="radio"
            :aria-checked="selected(option) === choice.value"
            :aria-label="choiceLabel(choice)"
            class="vpsc-image vpsc:flex vpsc:flex-col vpsc:items-center vpsc:gap-1 vpsc:rounded-xl! vpsc:border-2! vpsc:border-(--vs-line)! vpsc:bg-white! vpsc:p-1.5! vpsc:cursor-pointer"
            @click="set(option, choice.value)"
          >
            <span class="vpsc:flex vpsc:aspect-square vpsc:w-full vpsc:items-center vpsc:justify-center vpsc:overflow-hidden vpsc:rounded-lg vpsc:bg-gray-50">
              <img v-if="choice.image" :src="choice.image" :alt="choice.label" class="vpsc:max-h-full vpsc:max-w-full vpsc:object-contain" loading="lazy" />
            </span>
            <span class="vpsc:w-full vpsc:truncate vpsc:text-center vpsc:text-xs vpsc:text-(--vs-ink-2)">{{ choice.label }}</span>
            <span v-if="priceTag(choice.price, choice.per)" class="vpsc-price" :class="{ 'vpsc-price--discount': choice.price < 0 }" aria-hidden="true">{{ priceTag(choice.price, choice.per) }}</span>
          </button>
        </div>

        <input
          v-else-if="option.type === 'text'"
          type="text"
          class="vpsc:w-full vpsc:rounded-xl! vpsc:border! vpsc:border-(--vs-line)! vpsc:bg-white! vpsc:px-3! vpsc:py-2! vpsc:text-sm vpsc:shadow-none!"
          :class="option.transform === 'uppercase' ? 'vpsc:uppercase' : ''"
          :value="selected(option)"
          :maxlength="option.max_length"
          v-bind="{ placeholder: option.placeholder }"
          @input="set(option, inputValue($event))"
        />

        <p v-if="missing(option)" role="alert" class="vpsc:m-0! vpsc:text-xs vpsc:font-medium vpsc:text-red-600">{{ t('Please make a choice.') }}</p>
      </template>
    </div>
  </section>
</template>
